import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import type { RootState } from 'redux/types';
import { useNewOnboardingEligibility } from '../../hooks/useNewOnboardingEligibility';
import { setupOAB } from '../flat/LawsuitsMonitor/api';
import {
  getOabPending,
  confirmOab,
  parsePendingOab,
  OabPendingResponse,
} from '../flat/LawsuitsMonitor/api/oabConfirmation';
import { useOabEligibility } from 'app/services/oabEligibility';
import { useOabCaptureAnswer } from 'app/modules/OabCapture/useOabCaptureAnswer';
import { trackOabCaptureSurfaceViewed } from 'app/services/tracking/oabCapture/oabCaptureEvents';
import { useRegisterOab } from 'app/services/oab/useRegisterOab';
import * as auth from 'app/modules/Auth/_redux/authRedux';
import { ROUTES_PATH } from '../../constants/Routes';
import { isUnrecognizedPreferenceKeysError } from '../../services/userPreferences/isUnrecognizedPreferenceKeysError';
import { UserPreferences } from '../../services/userPreferences/types';
import {
  PendingOab,
  OnboardingOabStatus,
  OabSource,
  OabEditedFields,
} from './types';
import {
  trackOnboardingOabEdited,
  trackOnboardingOabPendingFound,
  trackOnboardingOabSkipped,
  trackOnboardingOabStepViewed,
  trackOnboardingOabSubmitted,
} from './trackOnboardingOabEvents';
import { professionToOnboardingBranch } from '../../services/tracking/onboarding/onboardingBranch';
import { trackOnboardingError } from '../../services/tracking/onboarding/onboardingErrorEvents';

const OAB_SCREEN_SURFACE = 'onboarding_oab_screen';
const NOT_FOUND = 404;
const ALREADY_LINKED_STATUSES = [409, 422];
const ONBOARDING_OAB_PENDING_QUERY_KEY = ['onboarding-oab-pending'];
const TEN_MINUTES = 1000 * 60 * 10;

export const OAB_NOT_FOUND_MESSAGE = 'OAB não encontrada.';
export const GENERIC_ERROR_MESSAGE =
  'Não foi possível validar sua OAB. Tente novamente.';

const isAlreadyLinkedStatus = (status?: number) =>
  status != null && ALREADY_LINKED_STATUSES.includes(status);

type CreditsSlice = { credits?: { isLoading?: boolean } };

export function useOnboardingOab() {
  const history = useHistory();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const registerOab = useRegisterOab();
  const userId = useSelector((state: RootState) => state.auth.user?.id);
  const isCreditsLoading = useSelector(
    (state: RootState & CreditsSlice) => state.credits?.isLoading ?? false,
  );
  const {
    isEligible,
    baseEligible,
    completed: onboardingOabCompleted,
    isSubscriptionLoading,
    isPollingCredits,
    flagResolved,
    isPreferencesLoading,
    isStudentLoading,
    preferences,
    updateUserPreferencesAsync,
  } = useNewOnboardingEligibility('onboardingOabCompleted');

  const onboardingOabSkipped = Boolean(
    (preferences as UserPreferences)?.onboardingOabSkipped,
  );
  // Declaring "not a lawyer" on the welcome screen settles this step on its own.
  const { profession, isEligibilityResolved, answerProfession } =
    useOabEligibility({ enabled: baseEligible });
  const branch = professionToOnboardingBranch(profession);
  const oabStepDone =
    onboardingOabCompleted || onboardingOabSkipped || profession === 'OTHER';

  // Rota /onboarding/oab fica fora do Layout Metronic, que é quem normalmente
  // dispara LOAD_CREDITS_RESUME. Sem isso, credits.isLoading fica true para sempre.
  // Nas demais rotas o gate também usa este hook, e o Layout já dispara — daí o pathname.
  const isOnboardingOabRoute = pathname === ROUTES_PATH.ONBOARDING.OAB;

  useEffect(() => {
    if (!isOnboardingOabRoute || !userId || !isCreditsLoading) {
      return;
    }

    dispatch({
      type: 'LOAD_CREDITS_RESUME',
      payload: { clientId: userId },
    });
  }, [dispatch, userId, isCreditsLoading, isOnboardingOabRoute]);

  const {
    data: pendingOabResponse,
    isLoading: isPendingOabLoading,
    isError: isPendingOabError,
    error: pendingOabError,
  } = useQuery<OabPendingResponse | null>({
    queryKey: [...ONBOARDING_OAB_PENDING_QUERY_KEY, userId],
    queryFn: getOabPending,
    enabled: isEligible,
    retry: false,
    staleTime: TEN_MINUTES,
  });

  const hasTrackedPendingFetchErrorRef = useRef(false);

  // Not gated by status: the lookup failure is the very thing to report, and status stays 'ready'.
  useEffect(() => {
    if (
      !isOnboardingOabRoute ||
      !isPendingOabError ||
      hasTrackedPendingFetchErrorRef.current
    ) {
      return;
    }

    hasTrackedPendingFetchErrorRef.current = true;
    trackOnboardingError('oab', 'pending_fetch', pendingOabError);
  }, [isOnboardingOabRoute, isPendingOabError, pendingOabError]);

  const pendingOab = useMemo(
    () => parsePendingOab(pendingOabResponse),
    [pendingOabResponse],
  );

  const [oabNumber, setOabNumberState] = useState('');
  const [uf, setUf] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isAskingLawyer, setIsAskingLawyer] = useState(false);

  // Baseline is state, not a ref: the edition detection has to see the pre-filled values in the
  // same commit that sets them, otherwise the async pre-fill reports a false edition on every visit.
  const [prefilledOab, setPrefilledOab] = useState<PendingOab | null>(null);

  useEffect(() => {
    if (pendingOab && prefilledOab?.id !== pendingOab.id) {
      setPrefilledOab(pendingOab);
      setOabNumberState(pendingOab.oabNumber);
      setUf(pendingOab.uf);
    }
  }, [pendingOab, prefilledOab]);

  const oabEditedFields: OabEditedFields = useMemo(() => {
    if (!prefilledOab) {
      return 'none';
    }

    const isNumberEdited = oabNumber !== prefilledOab.oabNumber;
    const isUfEdited = uf !== prefilledOab.uf;

    if (isNumberEdited && isUfEdited) {
      return 'both';
    }

    if (isNumberEdited) {
      return 'number';
    }

    return isUfEdited ? 'uf' : 'none';
  }, [prefilledOab, oabNumber, uf]);

  const hasTrackedEditedRef = useRef(false);

  useEffect(() => {
    if (
      !isOnboardingOabRoute ||
      oabEditedFields === 'none' ||
      hasTrackedEditedRef.current
    ) {
      return;
    }

    hasTrackedEditedRef.current = true;
    trackOnboardingOabEdited(oabEditedFields === 'uf' ? 'uf' : 'number', branch);
  }, [isOnboardingOabRoute, oabEditedFields, branch]);

  const setOabNumber = useCallback((value: string) => {
    setOabNumberState(value.replace(/\D/g, ''));
  }, []);

  const finishOnboarding = useCallback(
    async (oabSkipped?: boolean) => {
      if (oabSkipped) {
        try {
          await updateUserPreferencesAsync({ onboardingOabSkipped: true });
        } catch (error) {
          if (!isUnrecognizedPreferenceKeysError(error)) {
            throw error;
          }
          await updateUserPreferencesAsync({ onboardingOabCompleted: true });
          history.push(ROUTES_PATH.ONBOARDING.OFFICE);
          return;
        }
        history.push(ROUTES_PATH.ONBOARDING.PHOTO);
        return;
      }

      await updateUserPreferencesAsync({ onboardingOabCompleted: true });
      history.push(ROUTES_PATH.ONBOARDING.OFFICE);
    },
    [updateUserPreferencesAsync, history],
  );

  const finishAndLeave = useCallback(
    async (oabSkipped?: boolean) => {
      try {
        await finishOnboarding(oabSkipped);
        return true;
      } catch (error) {
        trackOnboardingError('oab', 'step_complete', error);
        setErrorMessage(GENERIC_ERROR_MESSAGE);
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [finishOnboarding],
  );

  const oabSource: OabSource = useMemo(() => {
    if (pendingOab) {
      return 'pending';
    }

    return isPendingOabError ? 'unknown' : 'manual';
  }, [pendingOab, isPendingOabError]);

  const submit = useCallback(async () => {
    setIsSubmitting(true);
    setErrorMessage('');
    try {
      await registerOab(() => setupOAB(oabNumber, uf, 'onboarding'));
    } catch (error) {
      const responseStatus = (error as { response?: { status?: number } })
        ?.response?.status;

      if (!isAlreadyLinkedStatus(responseStatus)) {
        trackOnboardingError('oab', 'oab_setup', error);
        setErrorMessage(
          responseStatus === NOT_FOUND
            ? OAB_NOT_FOUND_MESSAGE
            : GENERIC_ERROR_MESSAGE,
        );
        setIsSubmitting(false);
        return false;
      }

      // The OAB was already monitored, so setupOAB rejected before
      // useRegisterOab could refresh the user — but the backend still synced
      // the profile on this branch, and it is exactly the cohort the Perfil
      // used to show stale.
      dispatch(auth.actions.requestUser());
    }

    const succeeded = await finishAndLeave();

    if (succeeded) {
      trackOnboardingOabSubmitted(
        uf,
        Boolean(oabNumber),
        oabSource,
        oabEditedFields !== 'none',
        oabEditedFields,
        branch,
      );
    }

    return succeeded;
  }, [
    oabNumber,
    uf,
    finishAndLeave,
    oabSource,
    oabEditedFields,
    registerOab,
    dispatch,
    branch,
  ]);

  const confirmPending = useCallback(async () => {
    if (!pendingOab) {
      return false;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    try {
      await registerOab(() =>
        confirmOab(pendingOab.id, `${uf}${oabNumber}`, 'onboarding'),
      );
    } catch (error) {
      trackOnboardingError('oab', 'oab_confirm', error);
      setErrorMessage(GENERIC_ERROR_MESSAGE);
      setIsSubmitting(false);
      return false;
    }

    const succeeded = await finishAndLeave();

    if (succeeded) {
      trackOnboardingOabSubmitted(
        uf,
        Boolean(oabNumber),
        oabSource,
        oabEditedFields !== 'none',
        oabEditedFields,
        branch,
      );
    }

    return succeeded;
  }, [
    pendingOab,
    uf,
    oabNumber,
    finishAndLeave,
    oabSource,
    oabEditedFields,
    registerOab,
    branch,
  ]);

  const skip = useCallback(async () => {
    if (pendingOab) {
      trackOnboardingOabSkipped(branch);
      setIsAskingLawyer(true);
      return true;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    const succeeded = await finishAndLeave(true);

    if (succeeded) {
      trackOnboardingOabSkipped(branch);
    }

    return succeeded;
  }, [finishAndLeave, branch, pendingOab]);

  const onLawyerAnswered = useCallback(
    (isLawyer: boolean) => {
      if (isLawyer) {
        finishAndLeave(true);
        return;
      }

      history.push(ROUTES_PATH.ONBOARDING.OFFICE);
    },
    [finishAndLeave, history],
  );

  const { answer, hasAnswerFailed } = useOabCaptureAnswer({
    surface: OAB_SCREEN_SURFACE,
    answerProfession,
    onAnswered: onLawyerAnswered,
  });

  const status: OnboardingOabStatus = useMemo(() => {
    if (
      isStudentLoading ||
      isSubscriptionLoading ||
      !flagResolved ||
      isPreferencesLoading ||
      (baseEligible && !isEligibilityResolved)
    ) {
      return 'loading';
    }

    if (oabStepDone) {
      return 'completed';
    }

    if (!isEligible) {
      return 'ineligible';
    }

    if (isPendingOabLoading) {
      return 'loading';
    }

    return 'ready';
  }, [
    isStudentLoading,
    isSubscriptionLoading,
    flagResolved,
    isPreferencesLoading,
    baseEligible,
    isEligibilityResolved,
    oabStepDone,
    isEligible,
    isPendingOabLoading,
  ]);

  const hasTrackedStepViewedRef = useRef(false);

  useEffect(() => {
    if (
      !isOnboardingOabRoute ||
      status !== 'ready' ||
      hasTrackedStepViewedRef.current
    ) {
      return;
    }

    hasTrackedStepViewedRef.current = true;
    trackOnboardingOabStepViewed(branch);
  }, [isOnboardingOabRoute, status, branch]);

  const hasTrackedQuestionViewedRef = useRef(false);

  useEffect(() => {
    if (!isAskingLawyer || hasTrackedQuestionViewedRef.current) {
      return;
    }

    hasTrackedQuestionViewedRef.current = true;
    trackOabCaptureSurfaceViewed({
      surface: OAB_SCREEN_SURFACE,
      step: 'question',
    });
  }, [isAskingLawyer]);

  const hasTrackedPendingFoundRef = useRef(false);

  // Gated by status: the disabled query still serves the cached pending OAB on redirect-only mounts.
  useEffect(() => {
    if (
      !isOnboardingOabRoute ||
      status !== 'ready' ||
      !pendingOab ||
      hasTrackedPendingFoundRef.current
    ) {
      return;
    }

    hasTrackedPendingFoundRef.current = true;
    trackOnboardingOabPendingFound(pendingOab.uf, branch);
  }, [isOnboardingOabRoute, status, pendingOab, branch]);

  return {
    status,
    isPollingCredits,
    pendingOab,
    oabNumber,
    uf,
    setOabNumber,
    setUf,
    isSubmitting,
    errorMessage,
    submit,
    confirmPending,
    skip,
    isAskingLawyer,
    answer,
    hasAnswerFailed,
  };
}
